'use client'

import { makeDrupalMenuLinkContent } from '@/lib/utils';
import { DrupalMenuLinkContent } from 'next-drupal';
import React, { useEffect, useState } from 'react';
import { Menu } from './Menu';
import lunr from 'lunr';
import Mark from 'mark.js';
import { usePathname } from 'next/navigation';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';

interface SidebarMenuSearchProps {
  menus: DrupalMenuLinkContent[];
}
interface storeDataType {
  [key: string]: string | boolean | DrupalMenuLinkContent | undefined;
}

export const SidebarMenuSearch = (props: SidebarMenuSearchProps) => {
  const [query, setQuery] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const pathname = usePathname();

  // Creating lunr index which will be searched from the query
  const index = lunr(function () {
    this.field("title");
    this.field("description");
    this.ref("id");
    this.metadataWhitelist = ['position'];

    const addIndex = (data: DrupalMenuLinkContent) => {
      return {
        id: data.id,
        title: data.title,
        description: data.description,
      }
    }

    const createMenuIndex = (menus: DrupalMenuLinkContent[]) => {
      menus.forEach((sidebar) => {
        this.add(
          addIndex(sidebar)
        );

        if (sidebar.items && sidebar.items.length > 0) {
          createMenuIndex(sidebar.items ?? []);
        }
      })
    }

    // Indexing only the contents which needs to searched
    createMenuIndex(props.menus ?? []);
  });

  const addStoreData = (data: DrupalMenuLinkContent) => {
    return {
      'description': data.description,
      'enabled': data.enabled,
      'expanded': data.expanded,
      'id': data.id,
      'menu_name': data.menu_name,
      'meta': data.meta,
      'options': data.options,
      'parent': data.parent,
      'provider': data.provider,
      'route': data.route,
      'title': data.title,
      'type': data.type,
      'url': data.url,
      'weight': data.weight,
      'items': data.items
    }
  }

  // store is the data which will be displayed after a search is matched with index.
  const store: storeDataType = {};
  const createStore = (menus: DrupalMenuLinkContent[]) => {
    menus.forEach((sidebar) => {
      store[sidebar.id] = addStoreData(sidebar);
      if (sidebar.items && sidebar.items.length > 0) {
        createStore(sidebar.items ?? []);
      }
    })
  }

  createStore(props.menus ?? []);

  const useCustomLunr = (query: string, index: lunr.Index, store: string) => {
    const search = index.search(query);
    let storeArray = JSON.parse(store);
    const results: string[] = [];
    search.forEach((value) => {
      results.push(storeArray[value.ref]);
    })

    return results;
  }

  const results = useCustomLunr(query, index, JSON.stringify(store));
  const dto: DrupalMenuLinkContent[] = [];

  //converting to DrupalMenuLinkContent type
  results.forEach((value) => {
    dto.push(makeDrupalMenuLinkContent(value));
  })

  const doSearch = (search: string) => {
    search == '' ? setQuery('') : setQuery(search + '~1');
    setSearch(search);
  }

  const clearSearch = () => {
    setQuery('');
    setSearch('');
  }

  useEffect(() => {
    clearSearch();
  }, [pathname]);

  useEffect(() => {
    //highlighting searched terms
    const markInstance = new Mark(document.getElementById("sidebar-menu")!);
    markInstance.unmark({
      done: () => {
        markInstance.mark(search, { accuracy: "partially", separateWordSearch: false });
      }
    });
  }, [search])

  return (
    <div className="pr-12">
      <input placeholder="Search menu" value={search} onChange={(event) => { doSearch(event.target.value) }} type="text" className="print:hidden w-full p-3 mb-2 rounded border-gray-500 border placeholder-gray-600" id="sidebar-search" />
      <button className={`print:hidden bg-transparent text-blue-700 py-1 px-2 mb-2 font-semibold hover:underline ${query == '' ? 'hidden' : ''}`} onClick={clearSearch}>
        <small className="flex"><ChevronLeftIcon className='mr-1 w-4 overflow-visible' /> Back to menu</small>
      </button>
      <Menu menu={query == '' ? props.menus : dto} className="flex flex-col text-navy-600" id="sidebar-menu" query={query} />
    </div>
  )
}